import { useEffect, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDInputSo from "components/MDInputSo";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import DauTrang, { NhanDau } from "components/DauTrang";
import { oTieuDeCot, chuXuongDong } from "lib/kieuBangDuLieu";
import { SEMANTIC } from "assets/theme/avocadoBrand";
import { danhMucApi } from "api/danhMuc";
import { giaTheoNgayApi } from "api/giaTheoNgay";
import { giaBoWebsiteApi } from "api/giaBoWebsite";
import { nguoiDungApi } from "api/nguoiDung";
import { useAuth } from "context/AuthContext";
import useTour from "hooks/useTour";
import buocGiaTheoNgay from "tours/giaTheoNgay";

function ngayHomNay() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ngayCong(ngayStr, soNgay) {
  const d = new Date(`${ngayStr}T00:00:00`);
  d.setDate(d.getDate() + soNgay);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function ngayTruoc(ngayStr) {
  return ngayCong(ngayStr, -1);
}

function ngayVN(ngayStr) {
  const d = new Date(`${ngayStr}T00:00:00`);
  return d.toLocaleDateString("vi-VN");
}

function thoiGianVN(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("vi-VN", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function gioVN(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" });
}

// Người Việt thường gọi nhau bằng từ cuối trong họ tên (tên đệm/họ đứng
// trước) — lấy từ cuối để hiện gọn trong ô bảng, tên đầy đủ vẫn có trong
// tooltip khi hover/chạm.
function tenNgan(hoTen) {
  if (!hoTen) return "";
  const tu = hoTen.trim().split(/\s+/);
  return tu[tu.length - 1];
}

const TEN_THU = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

function thuNganVN(ngayStr) {
  const d = new Date(`${ngayStr}T00:00:00`);
  return TEN_THU[d.getDay()];
}

function ngayThangVN(ngayStr) {
  const d = new Date(`${ngayStr}T00:00:00`);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}

function GiaTheoNgay() {
  const { user, vaiTro } = useAuth();
  const coTheDayWeb = vaiTro === "admin" || vaiTro === "ban_giam_doc";
  const theme = useTheme();
  // Mobile/tablet hẹp: chỉ 3 cột ngày (đọc được); desktop: 8 ngày như trước.
  const laManHep = useMediaQuery(theme.breakpoints.down("md"));
  const soNgayBang = laManHep ? 3 : 8;
  const buocNhayNgay = laManHep ? 3 : 7;

  const [loaiBoList, setLoaiBoList] = useState([]);
  const [dangTaiLoaiBo, setDangTaiLoaiBo] = useState(true);

  // Sửa/thêm/xoá Loại bơ gộp chung ngay ở đây — không phải qua Danh mục nữa,
  // vì việc đầu tiên cần làm khi có giống bơ mới luôn là đặt giá cho nó.
  const [dangThemLoai, setDangThemLoai] = useState(false);
  const [tenLoaiMoi, setTenLoaiMoi] = useState("");
  const [dangSuaTenId, setDangSuaTenId] = useState(null);
  const [tenSua, setTenSua] = useState("");
  const [loiLoai, setLoiLoai] = useState("");

  // Đổi "ngay" sang 1 ngày cũ bất kỳ là tự xem được giá đã đặt hôm đó — xem
  // lại giá cũ và đặt giá dùng chung 1 ô ngày, không cần 2 khối riêng.
  const [ngay, setNgay] = useState(ngayHomNay());
  const [giaMap, setGiaMap] = useState({});
  const [giaHomQuaMap, setGiaHomQuaMap] = useState({});
  // Ai đặt giá của ngày đang xem, lúc nào — dữ liệu đã có sẵn trong tài liệu
  // giaTheoNgay (capNhatBoi/capNhatLuc), chỉ chưa hiện ra UI trước đây.
  const [capNhatMap, setCapNhatMap] = useState({});
  const [nguoiDungMap, setNguoiDungMap] = useState({});
  const [dangTaiGia, setDangTaiGia] = useState(true);
  const [dangLuuId, setDangLuuId] = useState(null);
  const [daLuuId, setDaLuuId] = useState(null);
  const [loi, setLoi] = useState("");
  const [canhBaoWeb, setCanhBaoWeb] = useState("");
  const [thongBaoWeb, setThongBaoWeb] = useState("");
  const [dangDayWeb, setDangDayWeb] = useState(false);
  const [cauHinhWeb, setCauHinhWeb] = useState(null);

  // Tab "Giá" (đặt/sửa giá 1 ngày) và tab "Lịch sử giá theo tháng" (xem lại
  // theo lịch) — 2 tab riêng thay vì xếp chồng 2 khối như trước.
  const [tab, setTab] = useState(0);

  // Lịch sử giá theo tháng — hàng = loại bơ, cột = N ngày liên tiếp kể từ
  // ngày chọn (N = 3 trên mobile / 8 trên PC). Mặc định lùi 3 ngày trước hôm
  // nay để vào trang là thấy vài ngày GẦN ĐÂY đã có giá thật.
  const [ngayBatDau, setNgayBatDau] = useState(() => ngayCong(ngayHomNay(), -3));
  const cacNgayTrongBang = Array.from({ length: soNgayBang }, (_, i) => ngayCong(ngayBatDau, i));
  // { [ngay]: { [maLoaiBo]: { donGia, capNhatBoi, capNhatLuc } } }
  const [giaKhoangMap, setGiaKhoangMap] = useState({});
  const [dangTaiThang, setDangTaiThang] = useState(false);

  useEffect(() => {
    setDangTaiLoaiBo(true);
    const huyLoai = danhMucApi.langNgheLoaiBo(
      (ds) => {
        setLoaiBoList(ds);
        setDangTaiLoaiBo(false);
      },
      (err) => {
        setLoi(err.message);
        setDangTaiLoaiBo(false);
      }
    );

    const huyNd = nguoiDungApi.langNgheDanhSach(
      (ds) => {
        setNguoiDungMap(Object.fromEntries(ds.map((nd) => [nd.id, nd.hoTen])));
      },
      () => {
        // Không chặn trang nếu tải danh sách người dùng lỗi — chỉ mất phần
        // hiện tên, vẫn hiện được uid thô thay thế (xem tenNguoiCapNhat).
      }
    );
    return () => {
      huyLoai();
      huyNd();
    };
  }, []);

  useEffect(() => {
    if (!coTheDayWeb) {
      setCauHinhWeb(null);
      return undefined;
    }
    let huy = false;
    giaBoWebsiteApi
      .layCauHinh()
      .then((cfg) => {
        if (!huy) setCauHinhWeb(cfg);
      })
      .catch(() => {
        if (!huy) setCauHinhWeb(null);
      });
    return () => {
      huy = true;
    };
  }, [coTheDayWeb]);

  useEffect(() => {
    setDangTaiGia(true);
    setLoi("");
    let homNayXong = false;
    let homQuaXong = false;
    const danhDauXong = () => {
      if (homNayXong && homQuaXong) setDangTaiGia(false);
    };

    const huyHomNay = giaTheoNgayApi.langNgheTheoNgay(
      ngay,
      (dsHomNay) => {
        const map = {};
        const capNhat = {};
        dsHomNay.forEach((g) => {
          map[g.maLoaiBo] = String(g.donGia);
          capNhat[g.maLoaiBo] = { capNhatBoi: g.capNhatBoi, capNhatLuc: g.capNhatLuc };
        });
        setGiaMap(map);
        setCapNhatMap(capNhat);
        homNayXong = true;
        danhDauXong();
      },
      (err) => {
        setLoi(err.message);
        homNayXong = true;
        danhDauXong();
      }
    );

    const huyHomQua = giaTheoNgayApi.langNgheTheoNgay(
      ngayTruoc(ngay),
      (dsHomQua) => {
        const mapHomQua = {};
        dsHomQua.forEach((g) => {
          mapHomQua[g.maLoaiBo] = g.donGia;
        });
        setGiaHomQuaMap(mapHomQua);
        homQuaXong = true;
        danhDauXong();
      },
      (err) => {
        setLoi(err.message);
        homQuaXong = true;
        danhDauXong();
      }
    );

    return () => {
      huyHomNay();
      huyHomQua();
    };
  }, [ngay]);

  function tenNguoiCapNhat(uid) {
    if (!uid) return null;
    return nguoiDungMap[uid] ?? uid;
  }

  useEffect(() => {
    setDangTaiThang(true);
    const ngayCuoi = cacNgayTrongBang[cacNgayTrongBang.length - 1];
    giaTheoNgayApi
      .trongKhoang(cacNgayTrongBang[0], ngayCuoi)
      .then((ds) => {
        const map = {};
        ds.forEach((g) => {
          if (!map[g.ngay]) map[g.ngay] = {};
          map[g.ngay][g.maLoaiBo] = {
            donGia: g.donGia,
            capNhatBoi: g.capNhatBoi,
            capNhatLuc: g.capNhatLuc,
          };
        });
        setGiaKhoangMap(map);
      })
      .catch((err) => setLoi(err.message))
      .finally(() => setDangTaiThang(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ngayBatDau, soNgayBang]);

  function doiTuan(soBuoc) {
    setNgayBatDau((cur) => ngayCong(cur, soBuoc * buocNhayNgay));
  }

  async function dayLenWebsite(ngayDay, { imLangThieuCauHinh = false } = {}) {
    if (!coTheDayWeb) return null;
    const cfg = cauHinhWeb || (await giaBoWebsiteApi.layCauHinh());
    if (!cfg?.secret || !cfg?.pushUrl) {
      if (!imLangThieuCauHinh) {
        throw new Error("Chưa cấu hình website báo giá — Admin vào Cài đặt → Báo giá WordPress.");
      }
      return null;
    }
    return giaBoWebsiteApi.dayGiaLenWebsite({ ngay: ngayDay, cauHinh: cfg });
  }

  async function luuGia(maLoaiBo) {
    const donGia = giaMap[maLoaiBo];
    if (!donGia || Number(donGia) <= 0) {
      setLoi("Nhập giá hợp lệ trước khi lưu.");
      return;
    }
    setLoi("");
    setCanhBaoWeb("");
    setThongBaoWeb("");
    setDangLuuId(maLoaiBo);
    try {
      const ketQua = await giaTheoNgayApi.dat({ maLoaiBo, ngay, donGia, capNhatBoi: user?.uid });
      setCapNhatMap((m) => ({
        ...m,
        [maLoaiBo]: { capNhatBoi: ketQua.capNhatBoi, capNhatLuc: ketQua.capNhatLuc },
      }));
      setDaLuuId(maLoaiBo);
      setTimeout(() => setDaLuuId((cur) => (cur === maLoaiBo ? null : cur)), 1500);

      if (coTheDayWeb && cauHinhWeb?.tuDongDay !== false) {
        try {
          const push = await dayLenWebsite(ngay, { imLangThieuCauHinh: true });
          if (push?.success) {
            setThongBaoWeb(push.message || `Đã đẩy ${push.saved_count ?? ""} loại lên website.`);
          }
        } catch (errPush) {
          setCanhBaoWeb(`Đã lưu giá. Đẩy web thất bại: ${errPush.message}`);
        }
      }
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangLuuId(null);
    }
  }

  async function bamDayLenWebsite() {
    setLoi("");
    setCanhBaoWeb("");
    setThongBaoWeb("");
    setDangDayWeb(true);
    try {
      const push = await dayLenWebsite(ngay);
      setThongBaoWeb(push?.message || `Đã đẩy ${push?.saved_count ?? ""} loại lên website.`);
    } catch (err) {
      setLoi(err.message);
    } finally {
      setDangDayWeb(false);
    }
  }

  async function themLoaiMoi(e) {
    e.preventDefault();
    if (!tenLoaiMoi.trim()) return;
    setLoiLoai("");
    try {
      await danhMucApi.taoLoaiBo(tenLoaiMoi.trim());
      setTenLoaiMoi("");
      setDangThemLoai(false);
    } catch (err) {
      setLoiLoai(err.message);
    }
  }

  function moSuaTen(lb) {
    setDangSuaTenId(lb.id);
    setTenSua(lb.tenLoai);
    setLoiLoai("");
  }

  async function luuTen(id) {
    if (!tenSua.trim()) return;
    try {
      await danhMucApi.suaLoaiBo(id, tenSua.trim());
      setDangSuaTenId(null);
    } catch (err) {
      setLoiLoai(err.message);
    }
  }

  async function xoaLoai(id) {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Xoá loại bơ này? Giá đã đặt cho các ngày trước vẫn giữ nguyên.")) return;
    try {
      await danhMucApi.xoaLoaiBo(id);
    } catch (err) {
      setLoiLoai(err.message);
    }
  }

  const dangTai = dangTaiLoaiBo || dangTaiGia;

  useTour("quan-ly-gia-theo-ngay", dangTai ? [] : buocGiaTheoNgay());

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <DauTrang
          nhan="Quản lý"
          tieuDe="Loại bơ & giá thu mua"
          moTa="Đặt đơn giá từng loại bơ cho một ngày cụ thể, và tra lại giá đã đặt các ngày trước."
        >
          {coTheDayWeb && tab === 0 && (
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={bamDayLenWebsite}
              disabled={dangDayWeb || dangTai}
            >
              <Icon sx={{ mr: 0.75 }}>cloud_upload</Icon>
              {dangDayWeb ? "Đang đẩy..." : "Đẩy lên website"}
            </MDButton>
          )}
        </DauTrang>

        <Grid container justifyContent="center">
          <Grid item xs={12} md={9} lg={7}>
            <Tabs
              id="tour-quan-ly-gia-theo-ngay-tabs"
              value={tab}
              onChange={(e, v) => setTab(v)}
              sx={{ mb: 2 }}
              textColor="info"
              indicatorColor="info"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              <Tab label="Giá" />
              <Tab label="Lịch sử giá theo tháng" />
            </Tabs>

            {tab === 0 && (
              <Card>
                <MDBox
                  id="tour-quan-ly-gia-theo-ngay-dat-gia"
                  px={2.5}
                  py={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox flex={1} minWidth="14rem">
                    <NhanDau>Đặt giá</NhanDau>
                    <MDTypography variant="h6">Loại bơ &amp; đơn giá</MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color="text">
                      Thêm/sửa/xoá loại bơ và đặt đơn giá cho 1 ngày cụ thể — form thu mua sẽ tự
                      điền theo giá của ngày hôm đó. Đổi ngày để xem lại giá đã đặt cho ngày cũ.
                    </MDTypography>
                  </MDBox>
                  <MDInput
                    type="date"
                    label="Ngày"
                    value={ngay}
                    onChange={(e) => setNgay(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </MDBox>

                {(loi || loiLoai) && (
                  <MDBox px={2.5} pb={1}>
                    <MDAlert color="error">{loi || loiLoai}</MDAlert>
                  </MDBox>
                )}
                {canhBaoWeb && (
                  <MDBox px={2.5} pb={1}>
                    <MDAlert color="warning">{canhBaoWeb}</MDAlert>
                  </MDBox>
                )}
                {thongBaoWeb && (
                  <MDBox px={2.5} pb={1}>
                    <MDAlert color="success">{thongBaoWeb}</MDAlert>
                  </MDBox>
                )}

                <MDBox id="tour-quan-ly-gia-theo-ngay-danh-sach">
                  {dangTai ? (
                    <MDBox px={2.5} pb={3}>
                      <MDTypography variant="button" color="text">
                        Đang tải...
                      </MDTypography>
                    </MDBox>
                  ) : loaiBoList.length === 0 ? (
                    <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                      <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>sell</Icon>
                      <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                        Chưa có loại bơ nào.
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                        Bấm “Thêm loại bơ” bên dưới để đặt giá cho giống bơ đầu tiên.
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <List sx={{ py: 0 }}>
                      {loaiBoList.map((lb, i) => (
                        <MDBox key={lb.id}>
                          <MDBox sx={{ py: 1.5, px: 2.5 }}>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              {dangSuaTenId === lb.id ? (
                                <MDBox display="flex" gap={1} alignItems="center" flex={1}>
                                  <MDInput
                                    value={tenSua}
                                    onChange={(e) => setTenSua(e.target.value)}
                                    autoFocus
                                    fullWidth
                                  />
                                  <MDButton size="small" color="info" onClick={() => luuTen(lb.id)}>
                                    Lưu
                                  </MDButton>
                                  <MDButton
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => setDangSuaTenId(null)}
                                  >
                                    Huỷ
                                  </MDButton>
                                </MDBox>
                              ) : (
                                <>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    sx={{ ...chuXuongDong, flex: 1, minWidth: 0 }}
                                  >
                                    {lb.tenLoai}
                                  </MDTypography>
                                  <MDBox display="flex" gap={0.5} flexShrink={0}>
                                    <MDButton
                                      size="small"
                                      variant="text"
                                      color="info"
                                      onClick={() => moSuaTen(lb)}
                                    >
                                      Sửa tên
                                    </MDButton>
                                    <MDButton
                                      size="small"
                                      variant="text"
                                      color="error"
                                      onClick={() => xoaLoai(lb.id)}
                                    >
                                      Xoá
                                    </MDButton>
                                  </MDBox>
                                </>
                              )}
                            </MDBox>
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="center"
                              flexWrap="wrap"
                              gap={1}
                            >
                              <MDTypography variant="caption" color="text">
                                Hôm qua ({ngayVN(ngayTruoc(ngay))}):{" "}
                                {giaHomQuaMap[lb.id] != null
                                  ? `${giaHomQuaMap[lb.id].toLocaleString("vi-VN")} đ/kg`
                                  : "chưa đặt"}
                              </MDTypography>
                              <MDBox
                                display="flex"
                                gap={1}
                                alignItems="center"
                                flexWrap="wrap"
                                sx={{ width: { xs: "100%", sm: "auto" } }}
                              >
                                <MDInputSo
                                  placeholder="Đơn giá (đ/kg)"
                                  value={giaMap[lb.id] ?? ""}
                                  onChange={(v) => setGiaMap((m) => ({ ...m, [lb.id]: v }))}
                                  sx={{ flex: { xs: "1 1 8rem", sm: "0 1 auto" }, minWidth: 0 }}
                                />
                                <MDButton
                                  size="small"
                                  color={daLuuId === lb.id ? "success" : "info"}
                                  onClick={() => luuGia(lb.id)}
                                  disabled={dangLuuId === lb.id}
                                  sx={{ width: { xs: "100%", sm: "auto" } }}
                                >
                                  {daLuuId === lb.id ? "Đã lưu" : "Lưu"}
                                </MDButton>
                              </MDBox>
                            </MDBox>
                            {capNhatMap[lb.id]?.capNhatBoi && (
                              <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                                Cập nhật bởi {tenNguoiCapNhat(capNhatMap[lb.id].capNhatBoi)}
                                {capNhatMap[lb.id].capNhatLuc &&
                                  ` · ${thoiGianVN(capNhatMap[lb.id].capNhatLuc)}`}
                              </MDTypography>
                            )}
                          </MDBox>
                          {i < loaiBoList.length - 1 && <Divider sx={{ my: 0 }} />}
                        </MDBox>
                      ))}
                    </List>
                  )}
                </MDBox>

                {!dangTai && (
                  <MDBox id="tour-quan-ly-gia-theo-ngay-them-loai" px={2.5} py={2}>
                    {dangThemLoai ? (
                      <MDBox component="form" onSubmit={themLoaiMoi} display="flex" gap={1}>
                        <MDInput
                          value={tenLoaiMoi}
                          onChange={(e) => setTenLoaiMoi(e.target.value)}
                          placeholder="Tên loại bơ mới..."
                          autoFocus
                          fullWidth
                        />
                        <MDButton type="submit" variant="gradient" color="info">
                          Lưu
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          onClick={() => setDangThemLoai(false)}
                        >
                          Huỷ
                        </MDButton>
                      </MDBox>
                    ) : (
                      <MDButton
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => setDangThemLoai(true)}
                      >
                        <Icon sx={{ mr: 0.5 }}>add</Icon>
                        Thêm loại bơ
                      </MDButton>
                    )}
                  </MDBox>
                )}
              </Card>
            )}

            {tab === 1 && (
              <Card>
                <MDBox
                  px={2.5}
                  py={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox flex={1} minWidth="14rem">
                    <NhanDau>Tra cứu</NhanDau>
                    <MDTypography variant="h6">Lịch sử giá theo tháng</MDTypography>
                    <MDTypography variant="button" fontWeight="regular" color="text">
                      Xem giá mọi loại bơ trong {soNgayBang} ngày liên tiếp kể từ ngày chọn
                      {laManHep ? " (mobile rút còn 3 ngày; xoay ngang hoặc PC để xem 8 ngày)" : ""}
                      . Bấm tiêu đề 1 cột để nhảy ngày đó sang tab &quot;Giá&quot; nếu cần sửa.
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <IconButton
                      size="small"
                      onClick={() => doiTuan(-1)}
                      aria-label={`Lùi ${buocNhayNgay} ngày`}
                    >
                      <Icon>chevron_left</Icon>
                    </IconButton>
                    <MDInput
                      type="date"
                      label="Từ ngày"
                      value={ngayBatDau}
                      onChange={(e) => setNgayBatDau(e.target.value)}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: { xs: "9.5rem", sm: "auto" } }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => doiTuan(1)}
                      aria-label={`Tới ${buocNhayNgay} ngày`}
                    >
                      <Icon>chevron_right</Icon>
                    </IconButton>
                  </MDBox>
                </MDBox>

                {loi && (
                  <MDBox px={2.5} pb={1}>
                    <MDAlert color="error">{loi}</MDAlert>
                  </MDBox>
                )}

                {loaiBoList.length === 0 ? (
                  <MDBox px={2.5} pb={4} pt={1} textAlign="center">
                    <Icon sx={{ color: SEMANTIC.textDisabled, fontSize: "2.5rem" }}>sell</Icon>
                    <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
                      Chưa có loại bơ nào.
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block" mt={0.5}>
                      Thêm loại bơ ở tab “Giá” trước khi tra lịch sử.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox pb={3} sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: { xs: "auto", md: "640px" } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={[
                              oTieuDeCot,
                              ({ palette }) => ({
                                position: "sticky",
                                left: 0,
                                zIndex: 2,
                                backgroundColor: palette.grey[100],
                              }),
                            ]}
                          >
                            Loại bơ
                          </TableCell>
                          {cacNgayTrongBang.map((ngayO) => {
                            const laHomNay = ngayO === ngayHomNay();
                            return (
                              <TableCell
                                key={ngayO}
                                align="center"
                                onClick={() => setNgay(ngayO)}
                                sx={[
                                  oTieuDeCot,
                                  ({ palette }) => ({
                                    cursor: "pointer",
                                    backgroundColor: laHomNay
                                      ? palette.grey[200]
                                      : palette.grey[100],
                                    "&:hover": { backgroundColor: palette.grey[200] },
                                  }),
                                ]}
                              >
                                <MDBox>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="medium"
                                    display="block"
                                    color={laHomNay ? "info" : "dark"}
                                  >
                                    {thuNganVN(ngayO)}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text">
                                    {ngayThangVN(ngayO)}
                                  </MDTypography>
                                </MDBox>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loaiBoList.map((lb) => (
                          <TableRow
                            key={lb.id}
                            sx={({ palette }) => ({
                              transition: "background-color 160ms ease",
                              "&:hover": { backgroundColor: palette.grey[100] },
                            })}
                          >
                            <TableCell
                              sx={({ palette }) => ({
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: palette.background.paper,
                              })}
                            >
                              <MDTypography variant="button" fontWeight="medium" sx={chuXuongDong}>
                                {lb.tenLoai}
                              </MDTypography>
                            </TableCell>
                            {cacNgayTrongBang.map((ngayO) => {
                              const info = giaKhoangMap[ngayO]?.[lb.id];
                              const oGia = (
                                <MDBox>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="medium"
                                    color={info ? "text" : "secondary"}
                                    display="block"
                                  >
                                    {info ? info.donGia.toLocaleString("vi-VN") : "—"}
                                  </MDTypography>
                                  {info?.capNhatBoi && (
                                    <MDTypography
                                      variant="caption"
                                      color="text"
                                      display="block"
                                      sx={{ fontSize: "0.65rem", opacity: 0.75 }}
                                    >
                                      {tenNgan(tenNguoiCapNhat(info.capNhatBoi))}
                                      {info.capNhatLuc && ` · ${gioVN(info.capNhatLuc)}`}
                                    </MDTypography>
                                  )}
                                </MDBox>
                              );
                              return (
                                <TableCell key={ngayO} align="center">
                                  {info?.capNhatBoi ? (
                                    <Tooltip
                                      title={`Cập nhật bởi ${tenNguoiCapNhat(info.capNhatBoi)}${
                                        info.capNhatLuc ? ` · ${thoiGianVN(info.capNhatLuc)}` : ""
                                      }`}
                                      arrow
                                    >
                                      {oGia}
                                    </Tooltip>
                                  ) : (
                                    oGia
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {dangTaiThang && (
                      <MDTypography variant="caption" color="text" mt={1} px={2.5} display="block">
                        Đang tải...
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </Card>
            )}
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default GiaTheoNgay;
